import React, { FC, ReactNode, useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import Dropdown from "antd/es/dropdown";
import { MenuProps } from "antd/es/menu";
import { MenuInfo } from "rc-menu/lib/interface";
import { useIntl } from "react-intl";

import Team from "@mapmycustomers/shared/types/Team";
import { nameComparator } from "@mapmycustomers/shared/util/comparator";
import { SearchField } from "@mapmycustomers/ui";

import RoundButton from "@app/component/RoundButton";
import { getTeams } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";

import styles from "./CommonFilter.module.scss";

const CLEAR_OPTION_VALUE = "clear";

export interface TypeFilterProps {
  onChange: (teamId?: Team["id"]) => void;
  value?: Team["id"];
}

interface Props extends TypeFilterProps {
  teams: Team[];
}

const TeamFilter: FC<Props> = ({ onChange, teams, value }) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const [search, setSearch] = useState("");

  const handleMenuClick = useCallback(
    (info: MenuInfo) => {
      analytics.using(["Team Filter"]);
      const value = info.key as string;
      if (value === CLEAR_OPTION_VALUE) {
        onChange(undefined);
      } else {
        onChange(parseInt(value, 10) as Team["id"]);
      }
      setSearch(""); // reset search after applying
    },
    [analytics, onChange]
  );

  const getDropdown = useCallback(
    (menu: ReactNode) => {
      return (
        <div className={styles.customDropdown}>
          <SearchField
            className={styles.search}
            onChange={setSearch}
            placeholder={intl.formatMessage({
              id: "onboarding.users.filters.teamFilter.search.placeholder",
              defaultMessage: "Search",
              description: "Search field placeholder in Teams Filter on the User Guides page",
            })}
            value={search}
          />
          {menu}
        </div>
      );
    },
    [intl, search]
  );

  const menuProps: MenuProps = useMemo(() => {
    const q = search.trim().toLowerCase();
    const items: MenuProps["items"] = [
      {
        className: styles.clearMenuItem,
        key: CLEAR_OPTION_VALUE,
        label: intl.formatMessage({
          id: "onboarding.users.filters.teams.options.clear",
          defaultMessage: "Clear",
          description: "Clear option in the Team filter button on the User Guides page",
        }),
      },
      ...teams
        .sort(nameComparator)
        .filter(({ name }) => name.toLowerCase().includes(q))
        .map((team) => ({ key: team.id, label: team.name })),
    ];
    return {
      activeKey: String(value),
      items,
      onClick: handleMenuClick,
    };
  }, [handleMenuClick, intl, teams, value, search]);

  const selectedTeam = teams.find(({ id }) => id === value);

  return (
    <Dropdown
      dropdownRender={getDropdown}
      menu={menuProps}
      overlayClassName={styles.dropdownOverlay}
      trigger={["click"]}
    >
      <RoundButton active={!!value} hasMenu>
        {intl.formatMessage(
          {
            id: "onboarding.users.filters.team.title",
            defaultMessage: "Team{hasValue, select, true {: {value}} other {}}",
            description: "Title of the Team filter button on the User Guides page",
          },
          {
            hasValue: !!selectedTeam,
            value: selectedTeam?.name,
          }
        )}
      </RoundButton>
    </Dropdown>
  );
};

const mapStateToProps = (state: RootState) => ({
  teams: getTeams(state),
});

export default connect(mapStateToProps)(TeamFilter);
